import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Select, DatePicker, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { n351Api, ComplaintItem } from '../../services/n351Api';
import './N351Form.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

// ---- Node options ----
const NODE_OPTIONS = [
  { value: '',    label: '全部' },
  { value: '80',  label: '節點 80' },
  { value: '801', label: '節點 801' },
  { value: '802', label: '節點 802' },
  { value: '81',  label: '節點 81' },
  { value: '811', label: '節點 811' },
  { value: '812', label: '節點 812' },
  { value: '813', label: '節點 813' },
  { value: '82',  label: '節點 82' },
  { value: '84',  label: '節點 84' },
];

// ---- Status badge renderer ----
const StatusBadge: React.FC<{ value: string }> = ({ value }) => {
  if (value === 'P') {
    return <span className="status-p">P</span>;
  }
  return <span className="status-empty">{value || '-'}</span>;
};

// ---- Component ----
const N351Form: React.FC = () => {
  const navigate = useNavigate();

  const [startNo, setStartNo]   = useState('');
  const [endNo, setEndNo]       = useState('');
  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const [node, setNode]         = useState('');
  const [items, setItems]       = useState<ComplaintItem[]>([]);
  const [loading, setLoading]   = useState(false);
  const [queried, setQueried]   = useState(false);

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await n351Api.query({
        startNo,
        endNo,
        startDate: dateRange[0],
        endDate:   dateRange[1],
        node,
      });
      setItems(res.items || []);
      setQueried(true);
      if ((res.items || []).length === 0) {
        message.info('查無符合條件的客訴單');
      } else {
        message.success(`查詢到 ${res.items.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo, dateRange, node]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Table columns ----
  const columns: ColumnsType<ComplaintItem> = [
    {
      title: '客訴單號',
      dataIndex: 'fn110_01',
      key: 'fn110_01',
      width: 100,
      fixed: 'left',
    },
    {
      title: '日期',
      dataIndex: 'fn110_11',
      key: 'fn110_11',
      width: 110,
    },
    {
      title: '部門',
      dataIndex: 'fn110_22',
      key: 'fn110_22',
      width: 80,
    },
    {
      title: '客戶',
      dataIndex: 'fn110_221',
      key: 'fn110_221',
      width: 80,
    },
    {
      title: '節點80',
      dataIndex: 'fn110_80',
      key: 'fn110_80',
      width: 70,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點801',
      dataIndex: 'fn110_801',
      key: 'fn110_801',
      width: 75,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點802',
      dataIndex: 'fn110_802',
      key: 'fn110_802',
      width: 75,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點81',
      dataIndex: 'fn110_81',
      key: 'fn110_81',
      width: 70,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點811',
      dataIndex: 'fn110_811',
      key: 'fn110_811',
      width: 75,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點812',
      dataIndex: 'fn110_812',
      key: 'fn110_812',
      width: 75,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點813',
      dataIndex: 'fn110_813',
      key: 'fn110_813',
      width: 75,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點82',
      dataIndex: 'fn110_82',
      key: 'fn110_82',
      width: 70,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '節點84',
      dataIndex: 'fn110_84',
      key: 'fn110_84',
      width: 70,
      align: 'center',
      render: (v: string) => <StatusBadge value={v} />,
    },
    {
      title: '說明',
      dataIndex: 'fn110_93',
      key: 'fn110_93',
      ellipsis: true,
    },
  ];

  // ---- Render ----
  return (
    <div className="n351-form">
      {loading && (
        <div className="loading-overlay">載入中...</div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                客訴管理
              </a>
              <span>/</span>
              <span className="current">客訴單追蹤查詢</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>客訴單追蹤查詢</h1>
            <span className="badge">N351</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>客訴單號 從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 130 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 130 }}
            allowClear
          />

          <div className="group-divider" />

          <label>日期範圍:</label>
          <RangePicker
            value={[
              dateRange[0] ? dayjs(dateRange[0]) : null,
              dateRange[1] ? dayjs(dateRange[1]) : null,
            ]}
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0] ? dates[0].format('YYYY-MM-DD') : '',
                  dates[1] ? dates[1].format('YYYY-MM-DD') : '',
                ]);
              } else {
                setDateRange(['', '']);
              }
            }}
            style={{ width: 230 }}
          />

          <div className="group-divider" />

          <label>流程節點:</label>
          <Select
            value={node}
            onChange={(v) => setNode(v)}
            style={{ width: 120 }}
          >
            {NODE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>

          <Button type="primary" onClick={onQuery} loading={loading}>
            查詢
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
              </div>
            </div>
          )}

          {queried ? (
            <Table<ComplaintItem>
              columns={columns}
              dataSource={items}
              rowKey="fn110_01"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              size="small"
              scroll={{ x: 1200, y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請設定查詢條件並點擊「查詢」
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default N351Form;
